import Link from "next/link";
import type { CSSProperties, ReactNode } from "react";
import { fraunces } from "@/fonts";

export const displayFont = fraunces;

/* ------------------------------------------------------------------ */
/*  Band – full-bleed section wrapper with centred, padded inner       */
/* ------------------------------------------------------------------ */

export function Band({
  children,
  className = "",
  innerClassName = "",
  id,
  style,
}: {
  children: ReactNode;
  className?: string;
  innerClassName?: string;
  id?: string;
  style?: CSSProperties;
}) {
  return (
    <section id={id} className={`w-full ${className}`} style={style}>
      <div
        className={`mx-auto w-full max-w-[90rem] px-5 sm:px-8 lg:px-12 ${innerClassName}`}
      >
        {children}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Eyebrow – small uppercase label above headings                     */
/* ------------------------------------------------------------------ */

export function Eyebrow({
  children,
  inverse = false,
  className = "",
}: {
  children: ReactNode;
  inverse?: boolean;
  className?: string;
}) {
  return (
    <p
      className={`text-[0.7rem] font-semibold uppercase tracking-[0.26em] ${
        inverse ? "storefront-text-primary-tint" : "storefront-text-muted"
      } ${className}`}
    >
      {children}
    </p>
  );
}

/* ------------------------------------------------------------------ */
/*  BandCta – pill-shaped call-to-action link                          */
/* ------------------------------------------------------------------ */

type ButtonTone = "primary" | "secondary" | "ghost";
type ButtonSize = "sm" | "md";

const toneStyles: Record<ButtonTone, string> = {
  primary:
    "storefront-bg-primary text-white shadow-[0_20px_36px_-18px_rgba(0,39,0,0.58)] hover-storefront-bg-primary-dark",
  secondary:
    "border storefront-border-soft bg-white storefront-text-heading hover:bg-[var(--storefront-primary)] hover:text-white hover:border-transparent",
  ghost:
    "border border-white/22 bg-white/8 text-white backdrop-blur-sm hover:bg-white hover:text-[#002700] hover:border-white",
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: "px-4 py-2.5 text-xs",
  md: "px-6 py-3.5 text-sm",
};

export function BandCta({
  href,
  label,
  tone = "primary",
  size = "md",
}: {
  href: string;
  label: string;
  tone?: ButtonTone;
  size?: ButtonSize;
}) {
  return (
    <Link
      href={href}
      className={`inline-flex items-center justify-center rounded-full font-semibold transition-all duration-200 hover:-translate-y-px hover:shadow-lg ${sizeStyles[size]} ${toneStyles[tone]}`}
    >
      {label}
    </Link>
  );
}

/* ------------------------------------------------------------------ */
/*  SoftFallback – placeholder when a product image is missing         */
/*  Image well is plain white; soft radial product-shadow hints at     */
/*  where the imagery will live, never a coloured/gradient surface.    */
/* ------------------------------------------------------------------ */

export function SoftFallback({ label = "Product photo" }: { label?: string } = {}) {
  return (
    <div
      role="img"
      aria-label={label}
      className="relative flex h-full w-full items-center justify-center bg-white"
    >
      <div
        aria-hidden="true"
        className="h-[62%] w-[60%] rounded-[50%]"
        style={{
          background:
            "radial-gradient(ellipse at 50% 55%, rgba(0,39,0,0.06) 0%, rgba(0,39,0,0.02) 55%, rgba(0,39,0,0) 75%)",
        }}
      />
      <span
        aria-hidden="true"
        className="absolute text-[10px] font-bold uppercase tracking-[0.22em] text-[rgba(16,35,19,0.28)]"
      >
        {label}
      </span>
    </div>
  );
}
